import { useEffect, useState } from 'react';
import { fetchLatestDownloads } from '../utils/dynamicDownloadManager';

/* Same behaviour as the host's Download.js: ask the downloads directory for the
   newest build per platform, and fall back to these pinned URLs when the
   listing can't be read. The fallback values are copied from the host and must
   stay in sync with it — they are what real users get whenever the scrape
   fails, so a stale entry here means a stale installer for everybody. */

const BASE = 'https://teamgrid.ai/downloads';

const FALLBACK_URLS = {
  windows: `${BASE}/TeamGrid%20Setup%202.0.0.exe`,
  mac: `${BASE}/TeamGrid-2.0.0.dmg`,
  'mac-intel': `${BASE}/TeamGrid-2.0.0.dmg`,
  'mac-arm64': `${BASE}/TeamGrid-2.0.0-arm64.dmg`,
  ubuntu: `${BASE}/team-grid-desktop_1.6.0_amd64.deb`,
  'ubuntu-arm64': `${BASE}/team-grid-desktop_1.6.0_amd64.deb`,
};

const FALLBACK_VERSIONS = {
  windows: '2.0.0',
  mac: '2.0.0',
  'mac-intel': '2.0.0',
  'mac-arm64': '2.0.0',
  ubuntu: '1.6.0',
  'ubuntu-arm64': '1.6.0',
};

/** Triggers the download the same way the host does: a synthetic <a download>. */
export function startDownload(url) {
  if (!url) return false;
  const link = document.createElement('a');
  link.href = url;
  const parts = url.split('/');
  link.download = decodeURIComponent(parts[parts.length - 1] || 'TeamGrid');
  link.target = '_self';
  link.rel = 'noopener noreferrer';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  return true;
}

export default function useDownloads() {
  const [urls, setUrls] = useState(FALLBACK_URLS);
  const [versions, setVersions] = useState(FALLBACK_VERSIONS);
  const [sizes, setSizes] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchLatestDownloads()
      .then((downloads) => {
        if (cancelled) return;
        const u = {};
        const v = {};
        const s = {};
        Object.keys(downloads).forEach((p) => {
          u[p] = downloads[p].url;
          v[p] = downloads[p].version;
          s[p] = downloads[p].size;
        });
        /* Merge over the fallback rather than replace it, so a platform missing
           from the listing still gets a working link instead of a dead button. */
        setUrls((prev) => ({ ...prev, ...u }));
        setVersions((prev) => ({ ...prev, ...v }));
        setSizes(s);
      })
      .catch(() => {
        /* keep the pinned fallback — same as the host */
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  return { urls, versions, sizes, loading, startDownload };
}
